import { defineStore } from 'pinia';
import API from '@/api';
import { merchantInterface } from '@/api/merchants';
import { useCurrentUserStore } from '@/stores/currentUser';

export const useMerchantsStore = defineStore('merchants', {
    state: (): { merchants: merchantInterface[], loaded: boolean } => ({
        merchants: [],
        loaded: false,
    }),
    actions: {
        async fetchMerchants(): Promise<void> {
            const currentUserStore = useCurrentUserStore();

            this.merchants = currentUserStore.userHasRoleOnCurrentServiceProvider()
                ? await API.merchants.fetchMerchants()
                : await API.users.fetchMerchantsForCurrentUser();

            this.loaded = true;
        },
        async refreshMerchant(): Promise<void> {
            await this.fetchMerchants();
        },
        async fetchMerchantById(merchantId: number): Promise<merchantInterface | undefined> {
            const existing = this.merchants.find(merchant => merchant.id === merchantId);
            if (existing !== undefined) {
                return existing;
            }

            const merchant = await API.merchants.fetchMerchant(merchantId);

            // A concurrent caller may have added the same merchant while this fetch was
            // in flight, so re-check before pushing to avoid a duplicate entry.
            const added = this.merchants.find(stored => stored.id === merchant.id);
            if (added !== undefined) {
                return added;
            }

            this.addMerchant(merchant);

            return merchant;
        },
        async fetchMerchantByUuid(uuid: string): Promise<merchantInterface> {
            const existing = this.merchants.find(merchant => merchant.uuid === uuid);
            if (existing !== undefined) {
                return existing;
            }

            const merchant = await API.merchants.fetchMerchant(uuid);

            // A concurrent caller may have added the same merchant while this fetch was
            // in flight, so re-check before pushing to avoid a duplicate entry.
            const added = this.merchants.find(stored => stored.uuid === merchant.uuid);
            if (added !== undefined) {
                return added;
            }

            this.addMerchant(merchant);

            return merchant;
        },
        addMerchant(value: merchantInterface): void {
            this.merchants.push(value);
        },
    },
});
